'use strict';

const { COST_CATEGORIES, normalizeFundingCategory, seasonalProfile, categoryMix: normalizeCategoryMix } = require('./budgetSeasonality');

// Scheduled approved funding, not reconciled cash expenditure. The source
// schedule remains authoritative; this module only estimates additional demand.
const MODEL_VERSION = 'seasonal-cost-pace-v3';
// One average seasonal month of the original annual plan regularizes early
// observations. This is an explicit provisional assumption, not a fitted prior.
const PRIOR_EXPOSURE = 1 / 12;
const MAX_SAFE = Number.MAX_SAFE_INTEGER;
const money = value => {
  const numeric = Number(value ?? 0);
  const result = Math.round(numeric * 100);
  if (!Number.isFinite(numeric) || !Number.isSafeInteger(result)) throw new Error('Invalid forecast amount.');
  return result;
};
const sum = numbers => numbers.reduce((total, value) => {
  const next = total + value;
  if (!Number.isFinite(next) || Math.abs(next) > MAX_SAFE) throw new Error('Forecast amount exceeds supported range.');
  return next;
}, 0);
const amount = value => value / 100;

function dateOnly(value) {
  const text = value instanceof Date ? new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(value) : String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) throw new Error('A valid forecast date is required.');
  const parsed = new Date(`${text}T00:00:00Z`);
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== text) throw new Error('Invalid forecast date.');
  return text;
}

function fiscalMonths(fiscalYear) {
  const match = /^(\d{4})-(\d{4})$/.exec(String(fiscalYear || ''));
  if (!match || Number(match[2]) !== Number(match[1]) + 1) throw new Error('Invalid forecast fiscal year.');
  return Array.from({ length: 12 }, (_, index) => new Date(Date.UTC(Number(match[1]), index + 3, 1)).toISOString().slice(0, 7));
}

// Largest-remainder allocation preserves cents and is deterministic on ties.
function distributeCents(total, weights) {
  if (!Number.isSafeInteger(total) || total < 0 || weights.some(value => !Number.isFinite(value) || value < 0)) {
    throw new Error('Invalid forecast distribution.');
  }
  const weightTotal = sum(weights);
  if (!weightTotal) {
    if (total) throw new Error('Forecast distribution has no eligible months.');
    return weights.map(() => 0);
  }
  const shares = weights.map((weight, index) => ({ index, exact: total * (weight / weightTotal) }));
  const result = shares.map(share => Math.floor(share.exact));
  const remaining = total - sum(result);
  const ordered = [...shares].sort((a, b) => (b.exact - Math.floor(b.exact)) - (a.exact - Math.floor(a.exact)) || a.index - b.index);
  for (let index = 0; index < remaining; index += 1) result[ordered[index % ordered.length].index] += 1;
  return result;
}

function denseMonths(months, values = []) {
  const index = new Map(months.map((month, position) => [month, position]));
  const result = months.map(() => 0);
  for (const value of values) {
    const position = index.get(String(value.month || ''));
    if (position === undefined) continue;
    const cents = money(value.amount);
    if (cents < 0) throw new Error('Known forecast commitments cannot be negative.');
    result[position] = sum([result[position], cents]);
  }
  return result;
}

function resultBase({ fiscalYear, asOf, allocation, known }) {
  return {
    modelVersion: MODEL_VERSION, fiscalYear, asOf, allocation: amount(allocation), knownCommitted: amount(sum(known)),
    status: 'insufficient_data', projectedCommitted: null, projectedRemaining: null,
    // No calibrated probability interval exists for the first-year assumptions.
    lowerCommitted: null, upperCommitted: null, lowerRemaining: null, upperRemaining: null,
    confidence: null, explanation: '', monthlyProjected: [],
    components: { knownCommitted: amount(sum(known)), pipeline: null, additionalDemand: null },
    evidence: { observedMonths: 0, sourceCount: 0, seasonality: 'provisional_cost_type', coverageVerified: false },
  };
}

function unavailable(base, explanation, status = 'insufficient_data') {
  return Object.assign({}, base, { status, explanation });
}

function available(base, { monthly, months, confidence, explanation, additional = 0 }) {
  const projected = sum(monthly);
  if (!Number.isSafeInteger(projected) || projected < money(base.knownCommitted)) throw new Error('Invalid forecast total.');
  const allocation = money(base.allocation);
  return Object.assign({}, base, { status: 'available', projectedCommitted: amount(projected), projectedRemaining: amount(allocation - projected),
    confidence, explanation, monthlyProjected: months.map((month, index) => ({ month, amount: amount(monthly[index]) })),
    components: { knownCommitted: base.knownCommitted, pipeline: 0, additionalDemand: amount(additional) },
  });
}

function remainingFractions(months, today) {
  const current = today.slice(0, 7);
  const [year, month, day] = today.split('-').map(Number);
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return months.map(value => value < current ? 0 : value === current ? (days - day + 1) / days : 1);
}

// Retained administration behavior: allocation is a provisional annual cost
// assumption where no annual entry is available, not a recorded commitment.
function allocationBaseline(base, known, months, today, explanation) {
  const gap = Math.max(0, money(base.allocation) - sum(known));
  const extra = distributeCents(gap, remainingFractions(months, today));
  return Object.assign(available(base, { monthly: known.map((value, index) => value + extra[index]),
    months, confidence: 'low', additional: gap, explanation }), { basis: 'allocation_baseline', baselinePotCount: 1 });
}

/**
 * Forecast a direct pot; sum children separately. monthlyCommitted remains the
 * complete authoritative fiscal schedule. observations are its categorized
 * occurrences: {id, sourceId, scheduledDate, amount, category, eligible:true}.
 * Late/manual current-year entries are valid scheduled evidence; recordedDate
 * only prevents using a later record in an earlier dated calculation. This is
 * not a historical backtest: revised snapshots may lack as-known history.
 *
 * baselineAllocation is the original annual plan; allocation is today's cap.
 * A transfer changes remaining/overspend, not the inferred rate of demand.
 * Pending requests are not added with invented conversion probabilities. Their
 * eventual approvals become known schedules; until then they are part of the
 * unknown future funding that the seasonal estimate already represents.
 */
function forecastBudgetPot({ fiscalYear, asOf = new Date(), allocation = 0, baselineAllocation = allocation,
  kind = 'client', monthlyCommitted = [], observations = [], observationStart,
  coverageVerified = false, salaryEntered = false, seasonality, categoryMix, validation = null } = {}) {
  const today = dateOnly(asOf);
  const months = fiscalMonths(fiscalYear);
  const monthIndex = new Map(months.map((month, index) => [month, index]));
  const known = denseMonths(months, monthlyCommitted);
  const knownTotal = sum(known);
  const allocated = money(allocation);
  const base = resultBase({ fiscalYear, asOf: today, allocation: allocated, known });
  if (today > `${months[11]}-31`) return unavailable(base, 'This fiscal year has ended.', 'closed');
  if (today < `${months[0]}-01`) return unavailable(base, 'This fiscal year has not started.');
  if (kind === 'other') {
    if (allocated === 0 && knownTotal === 0) return available(base, { monthly: known, months, confidence: 'high', explanation: 'No allocation or commitments.' });
    return allocationBaseline(base, known, months, today, 'Administration costs use the allocation where costs have not been entered.');
  }
  if (kind === 'salary') {
    if (!salaryEntered) return allocationBaseline(base, known, months, today, 'Salary costs use the allocation until an annual commitment is entered.');
    return available(base, { monthly: known, months, confidence: 'moderate', explanation: 'Uses the entered annual salary commitment.' });
  }
  if (kind !== 'client') return unavailable(base, 'This pot has no supported forecasting basis.');

  const initial = money(baselineAllocation);
  if (initial < 0) throw new Error('The initial funding baseline cannot be negative.');
  const mix = normalizeCategoryMix(categoryMix);
  const initialByCategory = distributeCents(initial, mix);
  const profile = seasonalProfile(seasonality);
  const start = observationStart ? dateOnly(observationStart) : null;
  const currentMonth = today.slice(0, 7);
  const observed = months.map(month => Boolean(start) && month < currentMonth && `${month}-01` >= start);
  const observedMonths = observed.filter(Boolean).length;
  const remaining = remainingFractions(months, today);
  const groups = Object.fromEntries(COST_CATEGORIES.map(category => [category, {
    known: months.map(() => 0), future: months.map(() => 0), past: months.map(() => 0),
  }]));
  const sources = new Set();
  const seen = new Map();
  const described = months.map(() => 0);
  for (const entry of observations) {
    if (entry.eligible !== true || entry.cancelled === true) continue;
    if (entry.recordedDate && dateOnly(entry.recordedDate) > today) continue;
    const identity = String(entry.id || '');
    const source = String(entry.sourceId || '');
    if (!identity || !source) continue;
    const scheduledDate = dateOnly(entry.scheduledDate);
    const position = monthIndex.get(scheduledDate.slice(0, 7));
    if (position === undefined) continue;
    const cents = money(entry.amount);
    if (cents < 0) throw new Error('Observed funding cannot be negative.');
    const category = normalizeFundingCategory(entry.category);
    const fingerprint = `${source}|${scheduledDate}|${cents}|${category}`;
    if (seen.has(identity)) {
      if (seen.get(identity) !== fingerprint) throw new Error('Conflicting forecast occurrence identity.');
      continue;
    }
    seen.set(identity, fingerprint);
    described[position] = sum([described[position], cents]);
    const group = groups[category];
    group.known[position] = sum([group.known[position], cents]);
    if (observed[position]) {
      group.past[position] = sum([group.past[position], cents]);
      if (cents > 0) sources.add(source);
    }
    // Prorated remaining-current-month expectation is compared only with
    // current-month commitments dated today or later, never with earlier ones.
    if (scheduledDate >= today) group.future[position] = sum([group.future[position], cents]);
  }
  if (described.some((value, index) => value > known[index])) throw new Error('Forecast observations exceed recorded commitments.');
  const undescribed = known.map((value, index) => value - described[index]);
  undescribed.forEach((value, index) => {
    // Preserve records lacking categorized evidence. They do not train demand;
    // without an exact current-month date treat them as still upcoming to avoid
    // inventing an additional copy. Confidence cannot be High with this gap.
    groups.other.known[index] = sum([groups.other.known[index], value]);
    if (months[index] >= currentMonth) groups.other.future[index] = sum([groups.other.future[index], value]);
  });

  // No activity with unverified coverage is missing evidence, not proof of zero
  // demand. Verified zero activity, on the other hand, can reduce the forecast.
  const learn = observedMonths > 0 && (sources.size > 0 || coverageVerified === true);
  const monthlyExtra = months.map(() => 0);
  const categoryEvidence = [];
  for (let index = 0; index < COST_CATEGORIES.length; index += 1) {
    const category = COST_CATEGORIES[index];
    const group = groups[category];
    const weights = profile.profiles[category];
    const exposure = sum(weights.filter((_, position) => observed[position]));
    const past = sum(group.past);
    const initialCategory = initialByCategory[index];
    const annualPace = learn && exposure > 0 ? (past + initialCategory * PRIOR_EXPOSURE) / (exposure + PRIOR_EXPOSURE) : initialCategory;
    const futureWeights = weights.map((weight, position) => weight * remaining[position]);
    const futureWeight = sum(futureWeights);
    const futureKnown = sum(group.future);
    const expectation = learn ? annualPace * futureWeight : initialCategory - sum(group.known);
    if (!Number.isFinite(expectation) || Math.abs(expectation) > MAX_SAFE || !Number.isFinite(annualPace) || annualPace > MAX_SAFE) {
      throw new Error('Projected demand exceeds supported range.');
    }
    const additional = Math.max(0, Math.round(learn ? expectation - futureKnown : expectation));
    // Known future tuition/support covers the category's whole remaining
    // envelope, including timing shifts. Residual amounts represent unknown
    // future awards, not another instalment attached to an existing course.
    let residualWeights = futureWeights.map((weight, position) => Math.max(0, annualPace * weight - group.future[position]));
    if (!sum(residualWeights)) residualWeights = futureWeights;
    // Only a no-evidence baseline needs an even fallback when an explicitly
    // supplied profile gives every remaining month zero weight.
    if (additional > 0 && !sum(residualWeights)) residualWeights = remaining;
    const extra = distributeCents(additional, residualWeights);
    extra.forEach((value, position) => { monthlyExtra[position] = sum([monthlyExtra[position], value]); });
    categoryEvidence.push({ category, baselineAllocation: amount(initialCategory), observedCommitted: amount(past),
      elapsedSeasonalShare: exposure, annualPace: amount(Math.round(annualPace)),
      knownFutureCommitted: amount(futureKnown), additionalDemand: amount(additional) });
  }
  const additional = sum(monthlyExtra);
  const monthly = known.map((value, index) => sum([value, monthlyExtra[index]]));
  const projected = sum(monthly);
  const estimatedShare = projected > 0 ? additional / projected : 0;
  const exposure = sum(categoryEvidence.map((entry, index) => entry.elapsedSeasonalShare * mix[index]));
  const activeMonths = months.filter((_, index) => sum(COST_CATEGORIES.map(category => groups[category].past[index])) > 0).length;
  const observedTotal = sum(categoryEvidence.map(entry => money(entry.observedCommitted)));
  const timingDifference = sum(COST_CATEGORIES.map((key, index) => {
    const group = groups[key];
    const total = sum(group.past);
    const elapsed = categoryEvidence[index].elapsedSeasonalShare;
    if (!elapsed) return total * 2;
    return sum(group.past.map((value, position) => observed[position]
      ? Math.abs(value - total * profile.profiles[key][position] / elapsed) : 0));
  }));
  // Total-variation distance of observed versus expected within-period timing.
  // A badly mismatched curve keeps qualitative confidence Low, even near March.
  const timingMismatch = observedTotal > 0 ? timingDifference / (2 * observedTotal) : 1;
  const describedCompletely = sum(undescribed) === 0;
  const evidenceComplete = coverageVerified === true && describedCompletely;
  const validated = evidenceComplete && profile.validated && validation?.completeYears >= 2 && validation?.sampleSize >= 20
    && Number.isFinite(validation?.relativeError) && validation.relativeError >= 0;
  let confidence = 'low';
  // These are transparent qualitative gates, not probabilities. First-year
  // broad evidence can earn Moderate; High requires validated complete history.
  if (learn && describedCompletely && observedMonths >= 6 && activeMonths >= 4 && sources.size >= 10
    && exposure >= 0.55 && estimatedShare <= 0.25 && timingMismatch <= 0.35) confidence = 'moderate';
  if (confidence === 'moderate' && validated && observedMonths >= 9 && sources.size >= 20 && exposure >= 0.8 && estimatedShare <= 0.15 && validation.relativeError <= 0.15) confidence = 'high';
  const result = available(base, { monthly, months, additional, confidence,
    explanation: learn ? 'Uses recorded funding and a provisional seasonal estimate of further funding.' : 'Uses the original annual funding allocation until there is enough recorded activity to estimate the pace.' });
  return Object.assign(result, { basis: learn ? 'seasonal_pace' : 'allocation_baseline', baselinePotCount: learn ? 0 : 1,
    activityWeight: learn ? exposure / (exposure + PRIOR_EXPOSURE) : 0,
    evidence: { observedMonths, activeMonths, sourceCount: sources.size, seasonality: profile.source,
      coverageVerified: coverageVerified === true, baselineAllocation: amount(initial), estimatedShare,
      elapsedSeasonalShare: exposure, timingMismatch, uncategorizedKnown: amount(sum(undescribed)), categories: categoryEvidence } });
}

function aggregateBudgetForecasts(forecasts = [], { allocation = 0, fiscalYear, asOf } = {}) {
  const entries = forecasts.filter(Boolean);
  const today = dateOnly(asOf || entries[0]?.asOf || new Date());
  const year = fiscalYear || entries[0]?.fiscalYear;
  const months = fiscalMonths(year);
  const known = sum(entries.map(entry => money(entry.knownCommitted)));
  const base = resultBase({ fiscalYear: year, asOf: today, allocation: money(allocation), known: [known] });
  if (today > `${months[11]}-31`) return unavailable(base, 'This fiscal year has ended.', 'closed');
  if (!entries.length || entries.length !== forecasts.length || entries.some(entry => entry.status !== 'available')) {
    return unavailable(base, 'Not all included pots have a year-end estimate.');
  }
  if (entries.some(entry => entry.fiscalYear !== year || entry.asOf !== today)) {
    return unavailable(base, 'The included forecasts do not cover the same fiscal year and date.');
  }
  const projected = sum(entries.map(entry => money(entry.projectedCommitted)));
  const monthly = denseMonths(months, entries.flatMap(entry => entry.monthlyProjected || []));
  if (sum(monthly) !== projected) throw new Error('Forecast months do not reconcile with the year-end amount.');
  const ranks = { low: 0, moderate: 1, high: 2 };
  const confidence = entries.reduce((weakest, entry) => (ranks[entry.confidence] ?? 0) < ranks[weakest] ? (entry.confidence || 'low') : weakest, 'high');
  const baselinePotCount = entries.reduce((total, entry) => total + (entry.baselinePotCount || 0), 0);
  return Object.assign(available(base, { monthly, months, confidence,
    additional: sum(entries.map(entry => money(entry.components?.additionalDemand))),
    explanation: 'Combines the estimates for the included budget pots.' }), { basis: 'combined', baselinePotCount });
}

module.exports = { MODEL_VERSION, forecastBudgetPot, aggregateBudgetForecasts,
  aggregateBudgetProjections: aggregateBudgetForecasts, distributeCents };
